import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Calendar, LogOut, Layers, X, Play, Check, Paperclip, Download, Box } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { useScheduleData } from "@/hooks/useScheduleData";
import { roomColor } from "@/components/schedule/FilterBar";
import { fmtTime } from "@/lib/format";
import { tasksApi, filesApi, formatApiError } from "@/lib/api";
import ProjectSwitcher from "@/components/schedule/ProjectSwitcher";

/**
 * Read-only mobile schedule view.
 *
 * Auto-shown when viewport ≤ 767px. Designed for crew checking timings on
 * phones during a build — minimal taps, glanceable info, zero edit affordances.
 *
 * Layout: app-shell (top status bar + project switcher + day picker) over a
 * vertical scroll list of the selected day's schedule_blocks.
 */
export default function MobileSchedule() {
  const { user, logout } = useAuth();
  const { projects, project, data, loading, selectProject, refresh } = useScheduleData();
  const [dayKey, setDayKey] = useState(todayKey());
  const [sheet, setSheet] = useState(null); // open task detail: { block, task, room }
  const listRef = useRef(null);
  const nowRowRef = useRef(null);

  // Bucket all schedule blocks by yyyy-mm-dd start date.
  const byDay = useMemo(() => {
    const m = new Map();
    for (const b of data.schedule_blocks || []) {
      if (!b.start_at) continue;
      const k = b.start_at.slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(b);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.start_at.localeCompare(b.start_at));
    return m;
  }, [data.schedule_blocks]);

  const days = useMemo(() => {
    // Use the project window if available; else fall back to today.
    if (project?.start_date && project?.end_date) {
      return enumerateDays(project.start_date, project.end_date);
    }
    return [todayKey()];
  }, [project?.start_date, project?.end_date]);

  // Clamp dayKey to project window.
  useEffect(() => {
    if (days.length && !days.includes(dayKey)) setDayKey(days[0]);
  }, [days, dayKey]);

  const taskById = useMemo(
    () => Object.fromEntries((data.tasks || []).map((t) => [t.id, t])),
    [data.tasks],
  );
  const roomById = useMemo(
    () => Object.fromEntries((data.rooms || []).map((r) => [r.id, r])),
    [data.rooms],
  );
  const equipmentById = useMemo(
    () => Object.fromEntries((data.equipment || []).map((e) => [e.id, e])),
    [data.equipment],
  );
  // Allocations grouped by room — used in the task detail sheet so a crew
  // member viewing a row can quickly see what gear is in that room.
  const allocationsByRoom = useMemo(() => {
    const m = new Map();
    for (const a of data.equipment_allocations || []) {
      if (!a.room_id) continue;
      if (!m.has(a.room_id)) m.set(a.room_id, []);
      m.get(a.room_id).push(a);
    }
    return m;
  }, [data.equipment_allocations]);

  /** Patch task.status and refresh. Returns the previous status so callers
   *  can offer an undo. */
  const updateTaskStatus = async (taskId, nextStatus) => {
    const t = taskById[taskId];
    const prev = t?.status || "planned";
    try {
      await tasksApi.update(taskId, { status: nextStatus });
      await refresh();
      return prev;
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to update status");
      throw e;
    }
  };

  const todaysBlocks = byDay.get(dayKey) || [];

  // Subtask grouping (mirrors PlanningView): hide child blocks from the flat
  // list and surface them indented under a synthesized parent group anchored
  // at the earliest child's start. See PlanningView.DayGroup.slots for the
  // full rationale.
  const todaysItems = useMemo(() => {
    const childBlocksByParent = new Map();
    const flat = [];
    for (const b of todaysBlocks) {
      const t = taskById[b.task_id];
      const parentId = t?.parent_task_id;
      if (parentId && taskById[parentId]) {
        if (!childBlocksByParent.has(parentId)) childBlocksByParent.set(parentId, []);
        childBlocksByParent.get(parentId).push(b);
      } else {
        flat.push({ kind: "block", block: b });
      }
    }
    for (const arr of childBlocksByParent.values()) {
      arr.sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
    }
    for (const [parentId, kids] of childBlocksByParent) {
      flat.push({
        kind: "parent_group",
        parentId,
        parentTask: taskById[parentId],
        children: kids,
        earliest_start: kids[0].start_at,
        latest_end: kids[kids.length - 1].end_at,
      });
    }
    // Interleave by start time so the list stays time-ordered.
    flat.sort((a, b) => {
      const aStart = a.kind === "parent_group" ? a.earliest_start : a.block.start_at;
      const bStart = b.kind === "parent_group" ? b.earliest_start : b.block.start_at;
      return String(aStart).localeCompare(String(bStart));
    });
    return flat;
  }, [todaysBlocks, taskById]);

  // Default-expanded — a Set of parent IDs that have been explicitly collapsed.
  const [collapsedParents, setCollapsedParents] = useState(() => new Set());
  const toggleParent = (parentId) => {
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId); else next.add(parentId);
      return next;
    });
  };
  const isExpanded = (parentId) => !collapsedParents.has(parentId);

  // Auto-scroll to "now" when viewing today.
  useEffect(() => {
    if (dayKey === todayKey() && nowRowRef.current) {
      nowRowRef.current.scrollIntoView({ behavior: "instant", block: "center" });
    }
  }, [dayKey, todaysBlocks.length]);

  const goPrev = () => {
    const i = days.indexOf(dayKey);
    if (i > 0) setDayKey(days[i - 1]);
  };
  const goNext = () => {
    const i = days.indexOf(dayKey);
    if (i >= 0 && i < days.length - 1) setDayKey(days[i + 1]);
  };
  const jumpToday = () => {
    const k = todayKey();
    if (days.includes(k)) setDayKey(k);
  };

  const isToday = dayKey === todayKey();
  const dayIdx = days.indexOf(dayKey);
  const now = new Date();
  // ID of the block "happening now" — used for the now-line marker. Works on
  // the flat block list because synthesized parent groups themselves aren't
  // schedulable; nowness can only attach to an actual block.
  const nowBlockId = todaysBlocks.find((b) => {
    if (!isToday) return null;
    const start = new Date(b.start_at);
    const end = b.end_at ? new Date(b.end_at) : new Date(start.getTime() + 60 * 60 * 1000);
    return now >= start && now <= end;
  })?.id || null;

  return (
    <div className="min-h-[100dvh] bg-bg-base flex flex-col" data-testid="mobile-schedule">
      {/* Top bar: brand + user */}
      <header className="px-3 py-2 border-b border-line-strong flex items-center gap-2 sticky top-0 bg-bg-base z-30">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 bg-amber" />
          <span className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">PS · MOBILE</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={logout}
          className="btn-ghost px-2 py-1"
          title="Sign out"
          data-testid="mobile-logout-btn"
        >
          <LogOut className="w-3 h-3" />
        </button>
      </header>

      {/* Project switcher + day picker */}
      <div className="px-3 py-2 border-b border-line space-y-2 bg-bg-base sticky top-[33px] z-20">
        <ProjectSwitcher
          projects={projects || []}
          currentProjectId={project?.id}
          onSelect={selectProject}
        />
        <div className="flex items-center gap-1.5" data-testid="mobile-day-picker">
          <button
            onClick={goPrev}
            disabled={dayIdx <= 0}
            className="btn-ghost px-2 py-1.5 disabled:opacity-30"
            data-testid="mobile-day-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center">
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute leading-none">
              {isToday ? "TODAY" : "DAY"}
            </div>
            <div className="mono text-sm text-ink font-semibold tabular-nums leading-tight mt-0.5">
              {fmtFullDate(dayKey)}
            </div>
          </div>
          <button
            onClick={goNext}
            disabled={dayIdx < 0 || dayIdx >= days.length - 1}
            className="btn-ghost px-2 py-1.5 disabled:opacity-30"
            data-testid="mobile-day-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {!isToday && (
            <button
              onClick={jumpToday}
              className="btn-ghost px-2 py-1.5"
              title="Jump to today"
              data-testid="mobile-jump-today"
            >
              <Layers className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Schedule list */}
      <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="mobile-schedule-list">
        {loading ? (
          <LoadingState />
        ) : todaysItems.length === 0 ? (
          <EmptyDay />
        ) : (
          <ul className="divide-y divide-line">
            {todaysItems.map((item) => {
              if (item.kind === "parent_group") {
                return (
                  <MobileParentGroup
                    key={`pg-${item.parentId}`}
                    group={item}
                    taskById={taskById}
                    roomById={roomById}
                    nowBlockId={nowBlockId}
                    nowRowRef={nowRowRef}
                    expanded={isExpanded(item.parentId)}
                    onToggle={() => toggleParent(item.parentId)}
                    onOpen={(b) => setSheet({ block: b, task: taskById[b.task_id], room: roomById[b.room_id] })}
                    onUpdateStatus={updateTaskStatus}
                  />
                );
              }
              const b = item.block;
              return (
                <BlockRow
                  key={b.id}
                  block={b}
                  task={taskById[b.task_id]}
                  room={roomById[b.room_id]}
                  isNow={b.id === nowBlockId}
                  rowRef={b.id === nowBlockId ? nowRowRef : null}
                  onOpen={() => setSheet({ block: b, task: taskById[b.task_id], room: roomById[b.room_id] })}
                  onUpdateStatus={updateTaskStatus}
                />
              );
            })}
          </ul>
        )}
        <div className="px-3 py-4 mono text-2xs uppercase tracking-widest text-ink-mute text-center">
          {todaysBlocks.length} BLOCK{todaysBlocks.length === 1 ? "" : "S"} · SWIPE → IN PROGRESS · SWIPE ← DONE
        </div>
        {user?.name && (
          <div className="px-3 pb-6 mono text-2xs tracking-widest text-ink-mute text-center">
            signed in as {user.name}
          </div>
        )}
      </div>
      {sheet && (
        <MobileTaskSheet
          block={sheet.block}
          task={sheet.task}
          room={sheet.room}
          allocations={sheet.room ? (allocationsByRoom.get(sheet.room.id) || []) : []}
          equipmentById={equipmentById}
          subtasks={(data.tasks || []).filter((t) => t.parent_task_id === sheet.task?.id && !t.deleted_at)}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

// Swipe thresholds.
// REVEAL_PX is the offset at which the action button is visually committed
// (shown at full size, ready to be tapped). FULL_COMMIT_FRAC * row-width =
// the swipe distance at which the action auto-fires on release.
const REVEAL_PX = 80;
const FULL_COMMIT_FRAC = 0.6;
const MAX_OFFSET = 160;

/**
 * Synthesized parent-task header for the mobile flat list. Renders the parent
 * title with its derived time window (min/max of children) + child-count
 * badge, and indents the children as full BlockRows (so swipe-to-action works
 * on each individual child) when expanded.
 *
 * The parent itself has no schedule block of its own (Option-B model), so
 * tapping it just toggles collapse — it doesn't open the task sheet. The
 * children remain individually tappable.
 */
function MobileParentGroup({ group, taskById, roomById, nowBlockId, nowRowRef, expanded, onToggle, onOpen, onUpdateStatus }) {
  const { parentTask, children, earliest_start, latest_end } = group;
  if (!parentTask) return null;
  return (
    <li className="bg-bg-base" data-testid={`mobile-parent-group-${group.parentId}`}>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-center gap-3 px-3 py-3 active:bg-bg-elevated/40 transition-colors"
        data-testid={`mobile-parent-toggle-${group.parentId}`}
      >
        <div className="w-14 shrink-0">
          <div className="mono text-base text-ink font-semibold tabular-nums leading-none">{fmtTime(earliest_start)}</div>
          <div className="mono text-2xs text-ink-mute tabular-nums leading-none mt-1">→ {fmtTime(latest_end)}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink truncate" title={parentTask.title}>{parentTask.title}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className="mono text-2xs uppercase tracking-widest border border-amber/50 text-amber px-1"
              title={`${children.length} subtask${children.length === 1 ? "" : "s"}`}
            >
              {children.length} ▸
            </span>
            <span className="mono text-2xs uppercase tracking-widest text-ink-mute">
              {expanded ? "TAP TO COLLAPSE" : "TAP TO EXPAND"}
            </span>
          </div>
        </div>
        <ChevronDown
          className={"w-4 h-4 text-ink-mute shrink-0 transition-transform " + (expanded ? "" : "-rotate-90")}
        />
      </button>
      {expanded && (
        <ul className="border-l-2 border-line ml-3 divide-y divide-line" data-testid={`mobile-parent-children-${group.parentId}`}>
          {children.map((b) => (
            <BlockRow
              key={b.id}
              block={b}
              task={taskById[b.task_id]}
              room={roomById[b.room_id]}
              isNow={b.id === nowBlockId}
              rowRef={b.id === nowBlockId ? nowRowRef : null}
              onOpen={() => onOpen(b)}
              onUpdateStatus={onUpdateStatus}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function BlockRow({ block, task, room, isNow, rowRef, onOpen, onUpdateStatus }) {
  const status = (task?.status || "planned").toLowerCase();
  const containerRef = useRef(null);
  // Swipe state — null when at rest; { startX, dx, rawDx, width, committed } otherwise.
  // `dx` is clamped for visual reveal; `rawDx` is the full distance for the
  // full-commit decision (so a hard swipe past the screen edge still fires).
  const [swipe, setSwipe] = useState(null);
  const offset = swipe ? swipe.dx : 0;
  const dir = offset > 6 ? 1 : offset < -6 ? -1 : 0;

  const onTouchStart = (e) => {
    if (!task) return;
    const t = e.touches[0];
    setSwipe({
      startX: t.clientX,
      dx: 0,
      rawDx: 0,
      width: containerRef.current?.offsetWidth || 320,
      committed: false,
    });
  };
  const onTouchMove = (e) => {
    if (!swipe) return;
    const t = e.touches[0];
    const raw = t.clientX - swipe.startX;
    const clamped = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, raw));
    setSwipe((s) => ({ ...s, dx: clamped, rawDx: raw }));
  };
  const onTouchEnd = async () => {
    if (!swipe) return;
    const { rawDx, dx, width } = swipe;
    const fullCommit = Math.abs(rawDx) >= width * FULL_COMMIT_FRAC;
    const revealed = Math.abs(dx) >= REVEAL_PX;
    if (fullCommit) {
      const action = rawDx > 0 ? "in_progress" : "done";
      await commitAction(action);
      setSwipe(null);
    } else if (revealed) {
      setSwipe((s) => ({ ...s, dx: rawDx > 0 ? REVEAL_PX : -REVEAL_PX, committed: false }));
    } else {
      setSwipe(null);
    }
  };

  const commitAction = async (nextStatus) => {
    if (!task) return;
    try {
      const prev = await onUpdateStatus(task.id, nextStatus);
      const label = nextStatus === "done" ? "Marked DONE" : "Marked IN PROGRESS";
      toast.success(label, {
        duration: 5000,
        action: {
          label: "UNDO",
          onClick: () => onUpdateStatus(task.id, prev),
        },
      });
    } catch {
      // onUpdateStatus toasts on failure.
    }
  };

  const cancelSwipe = () => setSwipe(null);

  // Tap behavior: if swipe is revealed and user taps anywhere outside the
  // action button, snap closed. If at rest, taps open the detail sheet.
  const onClickRow = (e) => {
    if (swipe) {
      // Tap during a revealed-swipe state collapses the swipe.
      e.preventDefault();
      cancelSwipe();
      return;
    }
    onOpen && onOpen();
  };

  return (
    <li
      ref={(el) => { containerRef.current = el; if (rowRef) rowRef.current = el; }}
      className={`relative overflow-hidden ${isNow ? "bg-amber/5 border-l-2 border-amber" : ""}`}
      data-testid={`mobile-block-${block.id}`}
    >
      {/* Background action surfaces — color reveals as the row slides. */}
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-between pointer-events-none"
        style={{ opacity: dir === 0 ? 0 : Math.min(1, Math.abs(offset) / REVEAL_PX) }}
      >
        {/* Right swipe: shows IN PROGRESS chip on the LEFT (where finger started). */}
        <div className="flex items-center gap-2 px-4 h-full bg-cyan/15">
          <Play className="w-4 h-4 text-cyan" />
          <span className="mono text-2xs uppercase tracking-widest text-cyan">IN PROGRESS</span>
        </div>
        {/* Left swipe: shows DONE chip on the RIGHT. */}
        <div className="flex items-center gap-2 px-4 h-full bg-lime/15 ml-auto">
          <span className="mono text-2xs uppercase tracking-widest text-lime">DONE</span>
          <Check className="w-4 h-4 text-lime" />
        </div>
      </div>

      {/* Foreground row — transforms with finger; tap commits action when revealed. */}
      <div
        className="relative bg-bg-base"
        style={{
          transform: `translateX(${offset}px)`,
          transition: swipe ? "none" : "transform 180ms ease",
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          onClick={onClickRow}
          className="w-full text-left flex items-center gap-3 px-3 py-3 active:bg-bg-elevated/40 transition-colors"
          data-testid={`mobile-row-${block.id}`}
        >
          {/* Time column */}
          <div className="w-14 shrink-0">
            <div className="mono text-base text-ink font-semibold tabular-nums leading-none">
              {fmtTime(block.start_at)}
            </div>
            {block.end_at && (
              <div className="mono text-2xs text-ink-mute tabular-nums leading-none mt-1">
                → {fmtTime(block.end_at)}
              </div>
            )}
          </div>
          {/* Title + room */}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-ink truncate" title={task?.title}>
              {task?.title || "—"}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              {room && (
                <span
                  className="mono text-2xs uppercase tracking-widest px-1.5 py-0.5"
                  style={{ background: roomColor(room.color_token), color: "#0A0A0A" }}
                >
                  {room.abbreviation}
                </span>
              )}
              <StatusBadge status={status} />
              {task?.is_locked && <span className="mono text-2xs text-amber">LOCKED</span>}
            </div>
          </div>
        </button>
      </div>

      {/* Revealed-action commit button. Only renders once the user has held
          the swipe past REVEAL_PX (iOS-Mail style); tapping commits, tapping
          the row collapses it. */}
      {swipe && Math.abs(swipe.dx) >= REVEAL_PX && (
        <button
          onClick={async () => {
            await commitAction(dir > 0 ? "in_progress" : "done");
            setSwipe(null);
          }}
          className={`absolute inset-y-0 px-4 mono text-2xs uppercase tracking-widest font-semibold ${
            dir > 0 ? "left-0 bg-cyan text-bg-base" : "right-0 bg-lime text-bg-base"
          }`}
          data-testid={`mobile-swipe-commit-${block.id}`}
        >
          {dir > 0 ? "START" : "DONE ✓"}
        </button>
      )}
    </li>
  );
}

function StatusBadge({ status }) {
  const tone =
    status === "done" ? "text-lime border-lime/40"
    : status === "in_progress" ? "text-cyan border-cyan/40"
    : status === "blocked" ? "text-red border-red/40"
    : "text-ink-mute border-line";
  return (
    <span className={`mono text-2xs uppercase tracking-widest px-1 py-px border ${tone}`}>
      {status.replace("_", " ")}
    </span>
  );
}

/**
 * Full-screen task detail bottom sheet shown when a mobile row is tapped.
 *
 * Sections (top to bottom):
 *   1. Header with close button + time + title + room badge + status
 *   2. Quick facts (duration, priority, notes, block notes)
 *   3. Room equipment list — every allocation in this room
 *   4. Attachments — read-only list of files on this task
 */
function MobileTaskSheet({ block, task, room, allocations, equipmentById, subtasks = [], onClose }) {
  // Block scroll on body when sheet is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!task) return null;
  const status = (task.status || "planned").toLowerCase();

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg-base" data-testid={`mobile-task-sheet-${block.id}`}>
      {/* Header */}
      <header className="px-3 py-2 border-b border-line-strong flex items-center gap-2">
        <button onClick={onClose} className="btn-ghost px-2 py-1" data-testid="mobile-task-sheet-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute leading-none">
            {fmtTime(block.start_at)}{block.end_at ? ` → ${fmtTime(block.end_at)}` : ""}
          </div>
          <div className="text-sm text-ink font-semibold truncate mt-0.5" title={task.title}>
            {task.title}
          </div>
        </div>
        <StatusBadge status={status} />
      </header>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
        {/* Quick facts */}
        <section className="space-y-1.5 text-xs">
          {room && (
            <Row
              label="ROOM"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="mono text-2xs uppercase tracking-widest px-1.5 py-0.5"
                    style={{ background: roomColor(room.color_token), color: "#0A0A0A" }}
                  >
                    {room.abbreviation}
                  </span>
                  <span>{room.name}</span>
                </span>
              }
            />
          )}
          {task.duration_min && <Row label="DURATION" value={`${task.duration_min}m`} />}
          {task.priority && task.priority !== "normal" && (
            <Row label="PRIORITY" value={task.priority.toUpperCase()} />
          )}
          {task.is_locked && <Row label="LOCKED" value="Yes" />}
        </section>

        {(task.notes || (block.notes && block.notes !== task.notes)) && (
          <section className="space-y-2">
            {task.notes && (
              <div>
                <div className="mono text-2xs uppercase tracking-widest text-ink-mute">NOTES</div>
                <div className="text-xs text-ink mt-1 whitespace-pre-wrap">{task.notes}</div>
              </div>
            )}
            {block.notes && block.notes !== task.notes && (
              <div>
                <div className="mono text-2xs uppercase tracking-widest text-ink-mute">BLOCK NOTES</div>
                <div className="text-xs text-ink mt-1 whitespace-pre-wrap">{block.notes}</div>
              </div>
            )}
          </section>
        )}

        {/* Subtasks — only when this task has children */}
        {subtasks.length > 0 && (
          <section data-testid="mobile-task-sheet-subtasks">
            <div className="flex items-center gap-1.5 mb-2">
              <h3 className="mono text-2xs uppercase tracking-widest text-amber">
                SUBTASKS
              </h3>
              <span className="mono text-2xs text-ink-mute ml-auto">{subtasks.length}</span>
            </div>
            <ul className="space-y-1.5">
              {subtasks.map((st) => (
                <li
                  key={st.id}
                  className="flex items-center gap-2 px-2 py-2 bg-bg-elevated border border-line"
                  data-testid={`mobile-subtask-${st.id}`}
                >
                  <span className="mono text-2xs text-ink-mute shrink-0">↳</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-ink truncate">{st.title}</div>
                    <div className="mono text-2xs text-ink-mute uppercase tracking-wider">
                      {st.duration_min ? `${st.duration_min}m` : "—"} · {(st.status || "planned").toUpperCase()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Room equipment */}
        <section data-testid="mobile-task-sheet-equipment">
          <div className="flex items-center gap-1.5 mb-2">
            <Box className="w-3 h-3 text-amber" />
            <h3 className="mono text-2xs uppercase tracking-widest text-amber">
              ROOM EQUIPMENT {room ? `· ${room.abbreviation}` : ""}
            </h3>
            <span className="mono text-2xs text-ink-mute ml-auto">{allocations.length}</span>
          </div>
          {!room ? (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute py-2 italic">— no room —</div>
          ) : allocations.length === 0 ? (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute py-2 italic">— nothing allocated —</div>
          ) : (
            <ul className="space-y-1.5">
              {allocations.map((a) => {
                const eq = equipmentById[a.equipment_id];
                if (!eq) return null;
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 px-2 py-2 bg-bg-elevated border border-line"
                    data-testid={`mobile-allocation-${a.id}`}
                  >
                    <span className="mono text-base tabular-nums text-amber w-10 shrink-0">{a.quantity}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-ink truncate">{eq.type || eq.name || "—"}</div>
                      <div className="mono text-2xs text-ink-mute uppercase tracking-wider">
                        {eq.category || "uncategorised"} · {a.status || "planned"}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Attachments */}
        <section data-testid="mobile-task-sheet-attachments">
          <div className="flex items-center gap-1.5 mb-2">
            <Paperclip className="w-3 h-3 text-amber" />
            <h3 className="mono text-2xs uppercase tracking-widest text-amber">ATTACHMENTS</h3>
          </div>
          <MobileAttachments taskId={task.id} />
        </section>
      </div>
    </div>
  );
}

/** Read-only attachments list for the mobile sheet. Tap a row to view / download. */
function MobileAttachments({ taskId }) {
  const [files, setFiles] = useState(null);
  useEffect(() => {
    let mounted = true;
    filesApi.listForTask(taskId)
      .then((list) => { if (mounted) setFiles(list || []); })
      .catch(() => { if (mounted) setFiles([]); });
    return () => { mounted = false; };
  }, [taskId]);

  if (files === null) {
    return <div className="mono text-2xs uppercase tracking-widest text-ink-mute py-2">LOADING…</div>;
  }
  if (files.length === 0) {
    return <div className="mono text-2xs uppercase tracking-widest text-ink-mute py-2 italic">— no attachments —</div>;
  }
  return (
    <ul className="space-y-1.5">
      {files.map((f) => (
        <li key={f.id} className="px-2 py-2 bg-bg-elevated border border-line flex items-center gap-2" data-testid={`mobile-attachment-${f.id}`}>
          {/^image\//.test(f.mime || "") ? (
            <a
              href={filesApi.downloadUrl(f.id)}
              target="_blank"
              rel="noreferrer"
              className="shrink-0"
            >
              <img
                src={filesApi.downloadUrl(f.id)}
                alt={f.filename}
                className="w-10 h-10 object-cover border border-line"
              />
            </a>
          ) : (
            <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-bg-base border border-line">
              <Paperclip className="w-4 h-4 text-ink-mute" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs text-ink truncate" title={f.filename}>{f.filename}</div>
            <div className="mono text-2xs text-ink-mute uppercase tracking-wider">
              {(f.kind || "file").toUpperCase()} · {fmtBytes(f.size_bytes)}
            </div>
          </div>
          <a
            href={filesApi.downloadUrl(f.id)}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost p-2"
            title="Download / open"
            data-testid={`mobile-attachment-download-${f.id}`}
          >
            <Download className="w-3 h-3" />
          </a>
        </li>
      ))}
    </ul>
  );
}

function fmtBytes(n) {
  if (!n) return "0";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="mono uppercase tracking-widest text-ink-mute w-16 shrink-0">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="px-3 py-8 text-center">
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute">LOADING SCHEDULE…</div>
    </div>
  );
}

function EmptyDay() {
  return (
    <div className="px-6 py-12 text-center" data-testid="mobile-empty-day">
      <Calendar className="w-6 h-6 mx-auto text-ink-mute" />
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-3">NOTHING SCHEDULED</div>
      <div className="text-xs text-ink-dim mt-1">No blocks on this day.</div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────

function todayKey() {
  const d = new Date();
  return [d.getFullYear(), pad(d.getMonth() + 1), pad(d.getDate())].join("-");
}

function pad(n) { return String(n).padStart(2, "0"); }

function enumerateDays(start, end) {
  const out = [];
  const s = parseISODate(start);
  const e = parseISODate(end);
  if (!s || !e || s > e) return out;
  const cur = new Date(s);
  while (cur <= e) {
    out.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function parseISODate(s) {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y) return null;
  return new Date(y, m - 1, d);
}

function fmtFullDate(key) {
  const d = parseISODate(key);
  if (!d) return key;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}
